import React, { useState } from "react";
import { Images, ZoomIn } from "lucide-react";
import ProjectImagesLightbox from "./ProjectImagesLightbox";
import { projectImageCardClass } from "./ProjectImagesCarousel";

export const projectGallerySkeletonClass =
  `${projectImageCardClass} animate-pulse rounded-2xl bg-slate-200`;

function ProjectImageCard({ src, index, onZoom }) {
  return (
    <article className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#1e6fcf]/40 hover:shadow-xl">
      <div className={projectImageCardClass}>
        <img
          src={src}
          alt={`SSA Techno project showcase ${index + 1}`}
          className="absolute inset-0 h-full w-full bg-white object-contain transition-transform duration-500 group-hover:scale-[1.02]"
          loading={index < 6 ? "eager" : "lazy"}
          decoding="async"
        />

        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-[#0a1a2f]/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        <button
          type="button"
          onClick={() => onZoom(index)}
          className="absolute inset-0 z-10 flex cursor-zoom-in items-center justify-center bg-[#0a1a2f]/0 opacity-0 transition-all duration-300 group-hover:bg-[#0a1a2f]/25 group-hover:opacity-100 group-focus-within:opacity-100"
          aria-label={`View project image ${index + 1} full size`}
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#0a1a2f] shadow-lg">
            <ZoomIn className="h-4 w-4" />
            View full image
          </span>
        </button>

        <span className="pointer-events-none absolute left-3 top-3 z-20 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold tracking-wider text-[#1758a8] shadow-sm">
          #{index + 1}
        </span>
      </div>
    </article>
  );
}

export default function ProjectImagesGallery({ images, title = "Our Projects" }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (!images.length) return null;

  return (
    <>
      <div className="mb-6 flex items-center justify-between gap-4 rounded-2xl border border-[#1e6fcf]/15 bg-gradient-to-r from-[#e8f2fc] to-white px-5 py-4 md:mb-8 md:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#1e6fcf]/10 text-[#1758a8]">
            <Images className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#0a1a2f]">
              {images.length} project showcase{images.length === 1 ? "" : "s"}
            </p>
            <p className="text-xs text-slate-500">Our latest project showcases</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
        {images.map((src, index) => (
          <ProjectImageCard
            key={`${src}-${index}`}
            src={src}
            index={index}
            onZoom={setLightboxIndex}
          />
        ))}
      </div>

      {lightboxIndex !== null && (
        <ProjectImagesLightbox
          images={images}
          activeIndex={lightboxIndex}
          title={title}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </>
  );
}
